import { PriceLevelInputDataInput, PriceLevelType, TargetType } from "~/gql/graphqlBase.generated";
import { VolumePoolFragment } from "~/gql/queries/volumePools.generated";
import { levelHasTargetId } from "~fulfill/sales/pricing/helpers/priceLevelTargetMatch";

export interface PoolLadderLevel {
	endDate?: string | null;
	levelType: PriceLevelType;
	priceLevelId: string;
	startDate: string;
	targetId: string;
	targetName?: string | null;
	type: TargetType;
	volumePool?: { code: string; name: string } | null;
	volumePoolId?: string | null;
}

export interface PoolLadderWindow {
	endDate?: string | null;
	levelType?: PriceLevelType | null;
	priceLevelId?: string | null;
	startDate: string;
	targetId: string;
	type: TargetType;
}

const EMPTY_SCOPE: PoolLadderScope = { currentPoolId: null, currentPoolName: "", siblingIds: [] };

export const isPoolableLevelType = (levelType: PriceLevelType | null | undefined): boolean =>
	levelType !== PriceLevelType.CategoryVolume;

export interface PoolLadderScope {
	currentPoolId: string | null;
	currentPoolName: string;
	siblingIds: string[];
}

const OPEN_ENDED_DAY = "9999-12-31";

const calendarDay = (value: string | null | undefined, fallback: string): string =>
	value ? value.slice(0, 10) : fallback;

export const priceLevelDatesOverlap = (
	a: { endDate?: string | null; startDate: string },
	b: { endDate?: string | null; startDate: string }
): boolean =>
	calendarDay(a.startDate, "") <= calendarDay(b.endDate, OPEN_ENDED_DAY) &&
	calendarDay(b.startDate, "") <= calendarDay(a.endDate, OPEN_ENDED_DAY);

export const normalizePoolName = (value: string | null | undefined): string => (value ?? "").trim().toLowerCase();

export const resolvePoolLadderScope = (
	levels: readonly PoolLadderLevel[],
	window: PoolLadderWindow
): PoolLadderScope => {
	if (!isPoolableLevelType(window.levelType)) return EMPTY_SCOPE;
	const siblings = levels.filter(
		(level) =>
			level.priceLevelId !== window.priceLevelId &&
			level.levelType === PriceLevelType.LinePrice &&
			level.type === window.type &&
			levelHasTargetId(level, window.targetId) &&
			priceLevelDatesOverlap(level, window)
	);
	const self = window.priceLevelId
		? levels.find((level) => level.priceLevelId === window.priceLevelId && level.levelType === PriceLevelType.LinePrice)
		: undefined;
	const pooled = self?.volumePoolId ? self : siblings.find((level) => !!level.volumePoolId);
	return {
		currentPoolId: pooled?.volumePoolId ?? null,
		currentPoolName: pooled?.volumePool?.name ?? "",
		siblingIds: siblings.map((level) => level.priceLevelId)
	};
};

interface PoolChangeRequest {
	chosenPoolName: string;
	isEditingExistingLevel: boolean;
	poolDiffers: boolean;
}

// The server refuses a pool-less rung beside pooled siblings, so an empty field on add joins the ladder.
export const shouldApplyPoolChange = ({
	chosenPoolName,
	isEditingExistingLevel,
	poolDiffers
}: PoolChangeRequest): boolean => poolDiffers && (isEditingExistingLevel || !!chosenPoolName.trim());

export const findVolumePoolByName = (
	pools: readonly VolumePoolFragment[],
	name: string
): VolumePoolFragment | undefined => {
	const key = normalizePoolName(name);
	if (!key) return undefined;
	return pools.find((pool) => normalizePoolName(pool.name) === key || normalizePoolName(pool.code) === key);
};

export const activeVolumePoolNames = (pools: readonly VolumePoolFragment[]): string[] =>
	pools
		.filter((pool) => !pool.retiredAt)
		.map((pool) => pool.name)
		.sort((a, b) => a.localeCompare(b));

export type PriceLevelPoolSaveStep =
	| {
			kind: "merge";
			levelOverrides: Pick<PriceLevelInputDataInput, "volumePoolId">;
			shouldRefreshGridOnFailure: boolean;
	  }
	| {
			kind: "assignPool";
			priceLevelIds: string[];
			shouldRefreshGridOnFailure: boolean;
			volumePoolId: string | null;
	  };

export interface PriceLevelPoolSaveRequest {
	hasPoolChanged: boolean;
	isEditing: boolean;
	ladder: PoolLadderScope;
	poolName: string;
	priceLevelId: string;
	resolvedPoolId: string | null;
}

const resolveTargetPoolId = ({
	hasPoolChanged,
	ladder,
	poolName,
	resolvedPoolId
}: PriceLevelPoolSaveRequest): string | null => {
	if (!hasPoolChanged) return ladder.currentPoolId;
	return poolName ? resolvedPoolId : null;
};

export const planPriceLevelPoolSave = (request: PriceLevelPoolSaveRequest): PriceLevelPoolSaveStep[] => {
	const { hasPoolChanged, isEditing, ladder, priceLevelId } = request;
	const targetPoolId = resolveTargetPoolId(request);

	if (isEditing) {
		const mergeStep: PriceLevelPoolSaveStep = { kind: "merge", levelOverrides: {}, shouldRefreshGridOnFailure: false };
		if (!hasPoolChanged) return [mergeStep];
		return [
			mergeStep,
			{
				kind: "assignPool",
				priceLevelIds: [...ladder.siblingIds, priceLevelId],
				shouldRefreshGridOnFailure: true,
				volumePoolId: targetPoolId
			}
		];
	}

	const mergeStep: PriceLevelPoolSaveStep = {
		kind: "merge",
		levelOverrides: { volumePoolId: targetPoolId ?? undefined },
		shouldRefreshGridOnFailure: true
	};
	if (!hasPoolChanged || ladder.siblingIds.length === 0) return [mergeStep];
	return [
		{
			kind: "assignPool",
			priceLevelIds: ladder.siblingIds,
			shouldRefreshGridOnFailure: false,
			volumePoolId: targetPoolId
		},
		mergeStep
	];
};

export interface SavedVolumePool {
	change: "created" | "reactivated";
	name: string;
}

export type PartialPoolSaveNotice =
	| {
			count: number;
			key:
				| "pricing.priceLevels.partialSave.levelSavedPoolNotMoved"
				| "pricing.priceLevels.partialSave.poolMovedLevelNotSaved";
	  }
	| { key: "pricing.priceLevels.partialSave.levelNotSaved" }
	| {
			key: "pricing.priceLevels.partialSave.poolCreated" | "pricing.priceLevels.partialSave.poolReactivated";
			pool: string;
	  };

const partialStepsNotice = (
	doneSteps: readonly PriceLevelPoolSaveStep[],
	failedStep: PriceLevelPoolSaveStep
): null | PartialPoolSaveNotice => {
	const movedPool = doneSteps.find((step) => step.kind === "assignPool");
	if (movedPool?.kind === "assignPool" && failedStep.kind === "merge") {
		return { count: movedPool.priceLevelIds.length, key: "pricing.priceLevels.partialSave.poolMovedLevelNotSaved" };
	}
	if (failedStep.kind === "assignPool" && doneSteps.some((step) => step.kind === "merge")) {
		return { count: failedStep.priceLevelIds.length, key: "pricing.priceLevels.partialSave.levelSavedPoolNotMoved" };
	}
	return null;
};

export const partialPoolSaveNotice = (
	doneSteps: readonly PriceLevelPoolSaveStep[],
	failedStep: PriceLevelPoolSaveStep,
	savedPool: null | SavedVolumePool
): PartialPoolSaveNotice[] => {
	const stepsNotice = partialStepsNotice(doneSteps, failedStep);
	if (!savedPool) return stepsNotice ? [stepsNotice] : [];
	const poolNotice: PartialPoolSaveNotice = {
		key:
			savedPool.change === "created"
				? "pricing.priceLevels.partialSave.poolCreated"
				: "pricing.priceLevels.partialSave.poolReactivated",
		pool: savedPool.name
	};
	return [poolNotice, stepsNotice ?? { key: "pricing.priceLevels.partialSave.levelNotSaved" }];
};
